import type { MouseEvent } from 'react';

import { BannerExistingEntitlements } from '@/components/BannerExistingEntitlements/BannerExistingEntitlements';
import { BannerExistingProduct } from '@/components/BannerExistingProduct/BannerExistingProduct';
import { BannerNotQualified } from '@/components/BannerNotQualified/BannerNotQualified';
import { BannerSmartPlusPur } from '@/components/BannerSmartPlusPur/BannerSmartPlusPur';
import { BannerSubscriptionOnHoliday } from '@/components/BannerSubscriptionOnHoliday/BannerSubscriptionOnHoliday';
import { Button } from '@/components/Button/Button';
import type { Bundle } from '@/components/Store.d';
import { languageStrings } from '@/l18n/de';
import type { BannerActionType } from '@/lib/banner';

interface ProductActionSectionProps {
	bannerAction: BannerActionType;
	bundle: Bundle | null;
	entitlementsAlreadyAcquired: boolean;
	handleClick: (e: MouseEvent<HTMLButtonElement>) => void;
	isEligibilityChecked: boolean;
	isSubmitted: boolean;
	pricingTextLength: number;
	printAccountNumber?: string;
	showActiveProductWarning: boolean;
	showSmartPlusPurHint?: boolean;
	showSubscriptionOnHoliday?: boolean;
	holidayEndDate?: string;
}

/**
 * Manages the display of banners and the main call-to-action button based on user state
 * @returns {JSX.Element} JSX element with appropriate banner or button
 */
const ProductActionSection = ({
	bannerAction,
	bundle,
	entitlementsAlreadyAcquired,
	handleClick,
	isEligibilityChecked,
	isSubmitted,
	pricingTextLength,
	printAccountNumber,
	showActiveProductWarning,
	showSmartPlusPurHint,
	showSubscriptionOnHoliday,
	holidayEndDate
}: ProductActionSectionProps): React.ReactElement => {
	const blocksCheckout = showActiveProductWarning || showSubscriptionOnHoliday;

	return (
		<>
			{showSubscriptionOnHoliday ? (
				<BannerSubscriptionOnHoliday holidayEndDate={holidayEndDate} />
			) : null}

			{Boolean(showActiveProductWarning) && !showSubscriptionOnHoliday ? (
				<BannerExistingProduct />
			) : null}

			{bannerAction === 'show-not-qualified' ? (
				<BannerNotQualified bundle={bundle} />
			) : bannerAction === 'show-upsell' && entitlementsAlreadyAcquired ? (
				<BannerExistingEntitlements bundle={bundle} printAccountNumber={printAccountNumber} />
			) : !blocksCheckout ? (
				<>
					{showSmartPlusPurHint ? <BannerSmartPlusPur /> : null}
					<Button
						ariaLabel="Weiter"
						className={[
							'product-cta',
							'form--button-cta',
							!pricingTextLength || !isEligibilityChecked || isSubmitted ? 'loading' : ''
						].join(' ')}
						disabled={!pricingTextLength || !isEligibilityChecked || isSubmitted || blocksCheckout}
						onClick={(e) => void handleClick(e)}
						role="button"
						type="button"
					>
						{languageStrings.buttons.next}
					</Button>
				</>
			) : null}
		</>
	);
};

export { ProductActionSection };
